import {
  hasOrganizationPermission,
  hasParticipantFeature,
  hasPlatformPermission,
} from '@/lib/access-policy'
import type {
  AuthorizationContext,
  FeatureKey,
  PermissionKey,
  PersonalGoal,
  ProgramType,
} from '@/types/access-control'

const EVERYONE_PROGRAMS = [
  'everyone',
  'everyone_severance',
  'everyone_plus',
  'everyone_plus_severance',
] as const satisfies readonly ProgramType[]

const CAREER_PROGRAMS = [
  ...EVERYONE_PROGRAMS,
  'internal_mobility',
] as const satisfies readonly ProgramType[]

const EVERYONE_PLUS_PROGRAMS = [
  'everyone_plus',
  'everyone_plus_severance',
] as const satisfies readonly ProgramType[]

export const INTERNAL_ROUTE_ACCESS = {
  'console.root': {
    site: 'console',
    path: '*',
    permission: 'platform.admin.access',
  },
  'console.users': {
    site: 'console',
    path: '/users',
    permission: 'authorization.manage',
  },
  'coach.root': {
    site: 'coach',
    path: '*',
    permission: 'coach.portal.access',
  },
  'coach-manager.root': {
    site: 'coach-manager',
    path: '*',
    permission: 'coach.manage',
  },
} as const satisfies Record<
  string,
  { site: 'console' | 'coach' | 'coach-manager'; path: string; permission: PermissionKey }
>

export type InternalRouteAccessId = keyof typeof INTERNAL_ROUTE_ACCESS

export const PORTAL_ROUTE_ACCESS = {
  'portal.root': { path: '*', permission: 'hr.portal.view' },
  'portal.people': { path: '/people', permission: 'hr.portal.view' },
  'portal.people.manage': { path: '/import', permission: 'hr.people.manage' },
  'portal.stakeholders.manage': { path: '/stakeholders', permission: 'hr.people.manage' },
  'portal.internal-jobs': { path: '/internal-jobs', permission: 'internal_mobility.view' },
  'portal.internal-jobs.manage': {
    path: '/internal-jobs',
    permission: 'internal_mobility.manage',
  },
  'portal.severance': { path: '/benefits', permission: 'hr.severance.view' },
  'portal.severance.manage': {
    path: '/severance-import',
    permission: 'hr.severance.manage',
  },
  'portal.documents.manage': { path: '/documents', permission: 'hr.documents.manage' },
} as const satisfies Record<string, { path: string; permission: PermissionKey }>

export type PortalRouteAccessId = keyof typeof PORTAL_ROUTE_ACCESS

export const PARTICIPANT_ROUTE_ACCESS = {
  'participant.root': { path: '*', feature: null, allowDuringWorkflow: true },
  'participant.profile': {
    path: '/profile',
    feature: null,
    allowDuringWorkflow: false,
    programs: CAREER_PROGRAMS,
  },
  'participant.external-jobs': {
    path: '/resume',
    feature: 'external_jobs',
    allowDuringWorkflow: false,
    programs: EVERYONE_PROGRAMS,
  },
  'participant.jobs': {
    path: '/opportunities',
    feature: ['external_jobs', 'internal_jobs'],
    allowDuringWorkflow: false,
    programs: CAREER_PROGRAMS,
  },
  'participant.internal-jobs': {
    path: '/opportunities',
    feature: 'internal_jobs',
    allowDuringWorkflow: false,
    programs: ['internal_mobility'],
  },
  'participant.skills-map': {
    path: '/skills-map',
    feature: 'skills_map',
    allowDuringWorkflow: false,
    programs: CAREER_PROGRAMS,
  },
  'participant.coaching': {
    path: '/coaching',
    feature: 'coaching',
    allowDuringWorkflow: false,
    programs: EVERYONE_PLUS_PROGRAMS,
  },
  'participant.start-business': {
    path: '/start-a-business',
    feature: 'coaching',
    allowDuringWorkflow: false,
    programs: EVERYONE_PLUS_PROGRAMS,
    goals: ['entrepreneurship'],
  },
  'participant.retirement': {
    path: '/retirement',
    feature: 'coaching',
    allowDuringWorkflow: false,
    programs: EVERYONE_PLUS_PROGRAMS,
    goals: ['retirement'],
  },
  'participant.benefits': {
    path: '/benefits',
    feature: 'benefits',
    allowDuringWorkflow: false,
    programs: [...EVERYONE_PROGRAMS, 'voluntary_exit'],
  },
  'participant.election': {
    path: '/benefits/election',
    feature: 'benefits',
    allowDuringWorkflow: true,
    programs: ['everyone_severance', 'everyone_plus_severance'],
  },
  'participant.learn': {
    path: '/learn',
    feature: 'learn',
    allowDuringWorkflow: false,
    programs: CAREER_PROGRAMS,
  },
} as const satisfies Record<
  string,
  {
    path: string
    feature: FeatureKey | readonly FeatureKey[] | null
    allowDuringWorkflow: boolean
    programs?: readonly ProgramType[]
    goals?: readonly PersonalGoal[]
  }
>

export type ParticipantRouteAccessId = keyof typeof PARTICIPANT_ROUTE_ACCESS
export type RouteAccessId = InternalRouteAccessId | PortalRouteAccessId | ParticipantRouteAccessId

export function isParticipantRouteAccessId(
  routeId: RouteAccessId,
): routeId is ParticipantRouteAccessId {
  return routeId in PARTICIPANT_ROUTE_ACCESS
}

export function canAccessInternalRoute(
  context: AuthorizationContext,
  routeId: InternalRouteAccessId,
): boolean {
  return hasPlatformPermission(context, INTERNAL_ROUTE_ACCESS[routeId].permission)
}

export function canAccessPortalRoute(
  context: AuthorizationContext,
  routeId: PortalRouteAccessId,
  organizationId: number | null | undefined,
): boolean {
  return (
    typeof organizationId === 'number' &&
    organizationId > 0 &&
    hasOrganizationPermission(context, PORTAL_ROUTE_ACCESS[routeId].permission, organizationId)
  )
}

export function canAccessParticipantRoute(
  context: AuthorizationContext,
  routeId: ParticipantRouteAccessId,
): boolean {
  const participant = context.participant
  if (!participant) return false
  if (
    !hasOrganizationPermission(context, 'participant.portal.access', participant.organization_id)
  ) {
    return false
  }

  const policy = PARTICIPANT_ROUTE_ACCESS[routeId]
  if (routeId === 'participant.root') return true
  if (routeId === 'participant.election' && participant.workflow_state === 'not_applicable') {
    return false
  }
  if (participant.workflow_restricted && !policy.allowDuringWorkflow) return false
  if (
    'programs' in policy &&
    !(policy.programs as readonly ProgramType[]).includes(participant.program_type)
  ) {
    return false
  }
  if (
    'goals' in policy &&
    !(policy.goals as readonly PersonalGoal[]).some((goal) =>
      participant.allowed_goals.includes(goal),
    )
  ) {
    return false
  }
  if (policy.feature === null) return true
  const features = Array.isArray(policy.feature) ? policy.feature : [policy.feature]
  return features.some((feature) =>
    hasParticipantFeature(context, feature, {
      allowDuringWorkflow: policy.allowDuringWorkflow,
    }),
  )
}

export function canAccessRoute(
  context: AuthorizationContext,
  routeId: RouteAccessId,
  organizationId?: number | null,
): boolean {
  if (routeId in PARTICIPANT_ROUTE_ACCESS) {
    return canAccessParticipantRoute(context, routeId as ParticipantRouteAccessId)
  }
  if (routeId in PORTAL_ROUTE_ACCESS) {
    return canAccessPortalRoute(context, routeId as PortalRouteAccessId, organizationId)
  }
  return canAccessInternalRoute(context, routeId as InternalRouteAccessId)
}
